import { AnimatePresence, motion } from 'motion/react';
import { Building, Cog, CreditCard, Info, Landmark, LogOut, MessageSquareText, PieChart, Repeat, Target, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { logout, User } from '../firebase';

interface NavigationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: string;
  setActiveTab: (tab: 'overview' | 'expenditure' | 'income' | 'debt' | 'support' | 'assets' | 'budget' | 'subscriptions' | 'savings') => void;
  onOpenSubscriptions: () => void;
  onOpenBillings: () => void;
  onOpenSettings: () => void;
  onOpenAbout: () => void;
  onOpenContactSupport: () => void;
  user: User | null;
  hasUnreadMessages: boolean;
}

export function NavigationDrawer({ isOpen, onClose, activeTab, setActiveTab, onOpenBillings, onOpenSettings, onOpenAbout, onOpenContactSupport, user, hasUnreadMessages }: NavigationDrawerProps) {
  const menuItems = [
    { id: 'assets', label: 'Assets', icon: Building },
    { id: 'debt', label: 'Debt', icon: Landmark },
    { id: 'budget', label: 'Budget', icon: PieChart },
    { id: 'savings', label: 'Savings', icon: Target },
    { id: 'subscriptions', label: 'Recurring', icon: Repeat },
  ] as const;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-[100]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 bottom-0 w-[280px] bg-white dark:bg-zinc-950 z-[101] shadow-2xl border-l border-zinc-100 dark:border-zinc-900 flex flex-col"
          >
            <div className="p-6 flex items-start justify-between border-b border-zinc-100 dark:border-zinc-900 bg-white/50 dark:bg-zinc-950/50 backdrop-blur-sm">
               <div className="flex flex-col gap-3">
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || 'User'}
                      className="w-12 h-12 rounded-full object-cover shadow-sm ring-2 ring-white dark:ring-zinc-800"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center shadow-lg shadow-indigo-200 dark:shadow-indigo-900/20 text-white font-bold text-lg">
                      {user?.displayName ? user.displayName[0].toUpperCase() : 'U'}
                    </div>
                  )}
                  <div className="space-y-0.5">
                    <h2 className="font-bold text-sm text-zinc-900 dark:text-zinc-100">{user?.displayName || 'User'}</h2>
                    <p className="text-[10px] text-zinc-500 truncate max-w-[180px]">{user?.email}</p>
                  </div>
               </div>
               <button onClick={onClose} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                 <X className="w-5 h-5 text-zinc-400" />
               </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              <div className="space-y-1">
                <p className="px-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2">Management</p>
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium",
                      activeTab === item.id 
                        ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100" 
                        : "text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100"
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="space-y-1">
                <p className="px-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2">App</p>
                <button
                    onClick={onOpenBillings}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100"
                  >
                    <CreditCard className="w-5 h-5" />
                    Billings
                </button>
                <button
                    onClick={onOpenSettings}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100"
                  >
                    <Cog className="w-5 h-5" />
                    Settings
                </button>
                <button
                    onClick={onOpenContactSupport}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100"
                  >
                    <div className="relative">
                      <MessageSquareText className="w-5 h-5" />
                      {hasUnreadMessages && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-white dark:border-zinc-950" />
                      )}
                    </div>
                    Support
                </button>
                <button
                    onClick={onOpenAbout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100"
                  >
                    <Info className="w-5 h-5" />
                    About
                </button>
              </div>
            </div>
            
            <div className="p-4 border-t border-zinc-100 dark:border-zinc-900">
               <button 
                 onClick={() => {
                   logout();
                   onClose();
                 }}
                 className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20"
               >
                 <LogOut className="w-5 h-5" />
                 Sign Out
               </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
